import { Suspense, lazy, ElementType, useEffect, Fragment } from 'react';
import {
  Navigate,
  useRoutes,
  useNavigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { ProductsEnum } from './@types/products';

import MainLayout from './layouts/main';
import GuestGuard from './guards/GuestGuard';
import AuthGuard from './guards/AuthGuard';
import Tabs from './components/Tabs';
import PageTitle from './components/PageTitle';
import products from 'data/products';

const Links = {
  profile: [
    {
      name: 'Interpretare Balanță',
      to: '/profile/interpretation-balance',
      active: false,
    },
    {
      name: 'Solicitare',
      to: '/profile/request',
      active: false,
    },
    {
      name: 'Ordin de plata',
      to: '/profile/payment-order',
      active: false,
    },
    {
      name: 'Altele',
      to: '/profile/other',
      active: false,
    },
    {
      name: 'Date profil',
      to: '/profile',
      active: false,
    },
  ],
};

const Loadable = (Component: ElementType) => (props: any) => {
  return (
    <Suspense fallback={<></>}>
      <Component {...props} />
    </Suspense>
  );
};

const HomePage = Loadable(lazy(() => import('./pages/home')));
const About = Loadable(lazy(() => import('./pages/About')));
const Contact = Loadable(lazy(() => import('./pages/Contact')));
const Pricing = Loadable(lazy(() => import('./pages/Pricing')));
const NotFound = Loadable(lazy(() => import('./pages/Page404')));
const Login = Loadable(lazy(() => import('./pages/auth/Login')));
const Register = Loadable(lazy(() => import('./pages/auth/Register')));
const ForgotPassword = Loadable(
  lazy(() => import('./pages/auth/forgot-password')),
);
const ResetPassword = Loadable(
  lazy(() => import('./pages/auth/reset-password')),
);
const Profile = Loadable(lazy(() => import('./pages/profile')));
const InterpretationBalance = Loadable(
  lazy(() => import('./pages/profile/interpretation-balance')),
);
const PaymentOrder = Loadable(
  lazy(() => import('./pages/profile/payment-order')),
);
const Request = Loadable(lazy(() => import('./pages/profile/request')));
const Other = Loadable(lazy(() => import('./pages/profile/other')));
const Legacy = Loadable(lazy(() => import('./pages/legacy')));
const LegacySlug = Loadable(lazy(() => import('./pages/legacy/slug')));
const Profit = Loadable(lazy(() => import('./pages/products/profit')));
const CashFlow = Loadable(lazy(() => import('./pages/products/cash-flow')));
const ProductRegister = Loadable(
  lazy(() => import('./pages/products/register')),
);
const Dividends = Loadable(lazy(() => import('./pages/products/dividends')));
const TVA = Loadable(lazy(() => import('./pages/products/tva')));
const GenerateStatement = Loadable(
  lazy(() => import('./pages/products/generate-statement')),
);
// const Partners = Loadable(lazy(() => import('./pages/products/partners')));
const Margin = Loadable(lazy(() => import('./pages/products/margin')));
const CustomPage = Loadable(lazy(() => import('./pages/custom-page')));

export default function Router() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const paths = pathname.split('/');
  const currentProduct = products[paths[2] as ProductsEnum] || {
    title: { second: '', first: '' },
  };
  const ProductGuard = !['TVA', 'Dividente'].includes(
    currentProduct.title.second,
  )
    ? AuthGuard
    : Fragment;

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [pathname]);

  return useRoutes([
    {
      path: 'auth',
      element: (
        <GuestGuard>
          <MainLayout>
            <Outlet />    {/* ← scoate div-urile container auth */}
          </MainLayout>
        </GuestGuard>
      ),
      children: [
        {
          path: 'login',
          element: <Login />,
        },
        {
          path: 'register',
          element: <Register />,
        },
        {
          path: 'forgot-password',
          element: <ForgotPassword />,
        },
        {
          path: 'reset-password/:token/:email',
          element: <ResetPassword />,
        },
      ],
    },

    {
      path: 'profile',
      element: (
        <AuthGuard>
          <MainLayout>
            <Tabs
              initialValue={pathname}
              tabs={Links.profile}
              handleChange={(value) => navigate(value)}
            >
              <Outlet />    {/* ← scoate section și paddingBottom */}
            </Tabs>
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        { element: <Profile />, index: true },
        { path: 'other', element: <Other /> },
        { path: 'payment-order', element: <PaymentOrder /> },
        { path: 'request', element: <Request /> },
        { path: 'interpretation-balance', element: <InterpretationBalance /> },
      ],
    },
    {
      path: 'products',
      element: (
        <ProductGuard>
          <MainLayout>
            {currentProduct.title.first && (
              <PageTitle
                title={`${currentProduct.title.first}  ${currentProduct.title.second}`}
                iconPath={`/assets/products/${currentProduct.icon}`}
              />
            )}
          </MainLayout>
        </ProductGuard>
      ),

      children: [
        { element: <Navigate to="/" replace />, index: true },
        { path: ProductsEnum.profit, element: <Profit /> },
        { path: ProductsEnum.cashFlow, element: <CashFlow /> },
        { path: ProductsEnum.register, element: <ProductRegister /> },
        { path: ProductsEnum.dividends, element: <Dividends /> },
        { path: ProductsEnum.tva, element: <TVA /> },
        { path: ProductsEnum.margin, element: <Margin /> },
        {
          path: ProductsEnum.generateStatement,
          element: <GenerateStatement />,
        },
        // { path: ProductsEnum.partners, element: <Partners /> },
      ],
    },
    {
      path: '/legacy',
      element: <MainLayout />,
      children: [
        { element: <Legacy />, index: true },
        { path: ':category', element: <Legacy /> },
        { path: 'info/:slug', element: <LegacySlug /> },
      ],
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { element: <HomePage />, index: true },
        { path: 'about', element: <About /> },
        { path: 'contact', element: <Contact /> },
        // STRIPE_DISABLED { path: 'pricing', element: <Pricing /> },
        { path: '404', element: <NotFound /> },
        { path: 'page/:name', element: <CustomPage /> },
      ],
    },
    { path: '*', element: <Navigate to="/" replace /> },
  ]);
}
